import {
  createCipheriv,
  createDecipheriv,
  createPrivateKey,
  createPublicKey,
  diffieHellman,
  generateKeyPairSync,
  hkdfSync,
  randomBytes,
  type KeyObject,
} from 'node:crypto'

/**
 * Sealed-package payload encryption.
 *
 * Content encryption: AES-256-GCM per entry, layout nonce(12) || ciphertext || tag(16).
 * Key wrapping ("x25519-hkdf-sha256-aes256gcm"): per recipient an ephemeral X25519 keypair;
 *   shared  = X25519(ephemeralPrivate, recipientPublic)
 *   kek     = HKDF-SHA256(shared, salt = ephemeralPublic || recipientPublic, info = "verentis-vpkg-wrap-v1", 32)
 *   wrapped = AES-256-GCM(kek) over the 32-byte content key (same nonce||ct||tag layout).
 * The registry digests cover the ENCRYPTED entry bytes, so integrity and signing are
 * unchanged for sealed packages.
 */
export const WRAP_ALGORITHM = 'x25519-hkdf-sha256-aes256gcm'
export const CONTENT_ALGORITHM = 'aes-256-gcm'
export const WRAP_INFO = 'verentis-vpkg-wrap-v1'

export interface EncryptionRecipient {
  /** Recipient label: "platform" (escrow / Node member reads) or a custom name. */
  id: string
  /** Base64 raw X25519 public key of the recipient. */
  publicKey: string
  /** Base64 raw X25519 public key of the wrap-time ephemeral keypair. */
  ephemeralPublicKey: string
  /** Base64 nonce||ciphertext||tag over the content key. */
  wrappedKey: string
}

export interface EncryptionManifest {
  algorithm: typeof CONTENT_ALGORITHM
  keyWrapping: typeof WRAP_ALGORITHM
  recipients: EncryptionRecipient[]
}

export interface SealingKeyPair {
  /** Base64 raw 32-byte X25519 public key. */
  publicKey: string
  /** Base64 raw 32-byte X25519 private key. */
  privateKey: string
}

export function generateSealingKeyPair(): SealingKeyPair {
  const { publicKey, privateKey } = generateKeyPairSync('x25519')
  return {
    publicKey: rawX25519Public(publicKey).toString('base64'),
    privateKey: rawX25519Private(privateKey).toString('base64'),
  }
}

export function generateContentKey(): Buffer {
  return randomBytes(32)
}

/** AES-256-GCM encrypt: returns nonce(12) || ciphertext || tag(16). */
export function encryptBytes(key: Buffer, plaintext: Buffer): Buffer {
  const nonce = randomBytes(12)
  const cipher = createCipheriv(CONTENT_ALGORITHM, key, nonce)
  const ciphertext = Buffer.concat([cipher.update(plaintext), cipher.final()])
  return Buffer.concat([nonce, ciphertext, cipher.getAuthTag()])
}

export function decryptBytes(key: Buffer, sealed: Buffer): Buffer {
  if (sealed.length < 12 + 16) throw new Error('Sealed data too short.')
  const nonce = sealed.subarray(0, 12)
  const tag = sealed.subarray(sealed.length - 16)
  const ciphertext = sealed.subarray(12, sealed.length - 16)
  const decipher = createDecipheriv(CONTENT_ALGORITHM, key, nonce)
  decipher.setAuthTag(tag)
  return Buffer.concat([decipher.update(ciphertext), decipher.final()])
}

/** Wraps the content key for one recipient. */
export function wrapContentKey(contentKey: Buffer, recipientId: string, recipientPublicKeyBase64: string): EncryptionRecipient {
  const recipientPublic = Buffer.from(recipientPublicKeyBase64, 'base64')
  const ephemeral = generateKeyPairSync('x25519')
  const ephemeralPublic = rawX25519Public(ephemeral.publicKey)

  const kek = deriveWrapKey(ephemeral.privateKey, publicKeyFromRaw(recipientPublic), ephemeralPublic, recipientPublic)
  return {
    id: recipientId,
    publicKey: recipientPublicKeyBase64,
    ephemeralPublicKey: ephemeralPublic.toString('base64'),
    wrappedKey: encryptBytes(kek, contentKey).toString('base64'),
  }
}

/** Unwraps the content key with a recipient's private key. Returns null when this recipient doesn't match. */
export function unwrapContentKey(manifest: EncryptionManifest, recipientPrivateKeyBase64: string): Buffer | null {
  const privateKey = privateKeyFromRaw(Buffer.from(recipientPrivateKeyBase64, 'base64'))
  const ownPublic = rawX25519Public(createPublicKey(privateKey))

  for (const recipient of manifest.recipients) {
    if (recipient.publicKey !== ownPublic.toString('base64')) continue
    const ephemeralPublic = Buffer.from(recipient.ephemeralPublicKey, 'base64')
    const kek = deriveWrapKey(privateKey, publicKeyFromRaw(ephemeralPublic), ephemeralPublic, ownPublic)
    try {
      return decryptBytes(kek, Buffer.from(recipient.wrappedKey, 'base64'))
    } catch {
      return null
    }
  }
  return null
}

function deriveWrapKey(privateKey: KeyObject, publicKey: KeyObject, ephemeralPublic: Buffer, recipientPublic: Buffer): Buffer {
  const shared = diffieHellman({ privateKey, publicKey })
  const salt = Buffer.concat([ephemeralPublic, recipientPublic])
  return Buffer.from(hkdfSync('sha256', shared, salt, WRAP_INFO, 32))
}

function rawX25519Public(key: KeyObject): Buffer {
  const jwk = key.export({ format: 'jwk' }) as { x?: string }
  if (!jwk.x) throw new Error('Failed to export X25519 public key.')
  return Buffer.from(jwk.x, 'base64url')
}

function rawX25519Private(key: KeyObject): Buffer {
  const jwk = key.export({ format: 'jwk' }) as { d?: string }
  if (!jwk.d) throw new Error('Failed to export X25519 private key.')
  return Buffer.from(jwk.d, 'base64url')
}

function publicKeyFromRaw(raw: Buffer): KeyObject {
  return createPublicKey({ key: { kty: 'OKP', crv: 'X25519', x: raw.toString('base64url') }, format: 'jwk' })
}

function privateKeyFromRaw(raw: Buffer): KeyObject {
  // PKCS#8 wrapping for X25519 private keys (RFC 8410): fixed prefix || 32-byte key.
  const der = Buffer.concat([Buffer.from('302e020100300506032b656e04220420', 'hex'), raw])
  return createPrivateKey({ key: der, format: 'der', type: 'pkcs8' })
}
